import React from 'react';
import { observer } from 'mobx-react';
import {
    FlexDiv,
    SpaceBetweenDiv,
    Block,
    Image,
    TextDefault,
    TextLead,
    Heading2,
    palette,
    ThemedButton,
    TextInput,
    CheckboxInput,
} from '@awarego-ui/components';
import {ButtonBase} from "@mui/material";
import Link from "@mui/material/Link";
import ThemedSpinner from '../themed/ThemedSpinner';
import {
    ThemedNav,
    ThemedNavItem,
    ThemedNavLink,
} from '../themed/ThemedComponents';
import PlayerCssEditor from './PlayerCssEditor';

function readUi(company, key, fallback = '') {
    if (!company) return fallback;
    const fromUi = company.uiSettings?.[key];
    if (fromUi != null && fromUi !== '') return fromUi;
    const settings = company.settings;
    if (settings && !Array.isArray(settings) && settings[key] != null) {
        return settings[key];
    }
    if (Array.isArray(settings)) {
        const row = settings.find((s) => s.name === key);
        if (row?.value != null) return row.value;
    }
    return fallback;
}

function CompanyAppearanceEditor({
    onEditLogo,
    onEditColor,
    onResetToDefaults,
    onSavePlayer,
    company_id,
    company,
    saving,
}) {
    const [activeNavItem, setActiveNavItem] = React.useState(0);
    const [fontFamily, setFontFamily] = React.useState(
        () => readUi(company, 'ui.font.family')
    );
    const [fontUrl, setFontUrl] = React.useState(
        () => readUi(company, 'ui.font.url')
    );
    const [hideClose, setHideClose] = React.useState(
        () => readUi(company, 'ui.player.hideClose') === '1'
    );
    const [customCss, setCustomCss] = React.useState(
        () => readUi(company, 'ui.my.customCss')
    );

    React.useEffect(() => {
        setFontFamily(readUi(company, 'ui.font.family'));
        setFontUrl(readUi(company, 'ui.font.url'));
        setHideClose(readUi(company, 'ui.player.hideClose') === '1');
        setCustomCss(readUi(company, 'ui.my.customCss'));
    }, [company?.company_id]);
    
    const primarySetting =
        company.uiSettings?.['ui.colors.primary'] ??
        company.settings?.['ui.colors.primary'] ??
        null;

    const secondarySetting =
        company.uiSettings?.['ui.colors.secondary'] ??
        company.settings?.['ui.colors.secondary'] ??
        null;

    return (
        <FlexDiv>
            <Block>
                <FlexDiv column>
                    <Heading2>Customize appearance</Heading2>
                    <TextDefault>
                        Change the appearance of {company.company_name} for
                        employees (employee view).
                    </TextDefault>
                </FlexDiv>
                <SpaceBetweenDiv bottomBorder py={16} mt={8}>
                    <FlexDiv column mr={16}>
                        <TextLead bold>Company Logo</TextLead>
                        <TextDefault>
                            Set a custom logo that will be shown on employee
                            view when they sign in.
                        </TextDefault>
                    </FlexDiv>
                    <ButtonBase
                        onClick={() => onEditLogo(company_id)}
                        disableRipple={true}
                        style={{flexDirection: 'column'}}
                    >
                        {company.logo_name ? (
                            <Image
                                width={150}
                                src={
                                    import.meta.env.REACT_APP_RESOURCES_URL +
                                    '/' +
                                    import.meta.env
                                        .REACT_APP_COMPANY_RESOURCES_DIR +
                                    '/' +
                                    company.logo_name
                                }
                                alt="Your logo"
                            />
                        ) : (
                            <Image
                                width={50}
                                src="/static/img/logo-image.svg"
                                alt="Add your logo"
                            />
                        )}
                        <TextDefault themed bold>
                            Change
                        </TextDefault>
                    </ButtonBase>
                </SpaceBetweenDiv>
                <SpaceBetweenDiv bottomBorder py={16} mb={24}>
                    <FlexDiv column mr={16}>
                        <TextLead bold>Colors</TextLead>
                        <TextDefault>
                            Change the colors of main buttons, links and navigation highlights to your
                            brand colors.
                        </TextDefault>
                    </FlexDiv>
                    <ButtonBase
                        onClick={() => onEditColor(company_id)}
                        disableRipple={true}
                        style={{flexDirection: 'column'}}
                    >
                        <FlexDiv gap={8}>
                            <FlexDiv
                                backgroundColor={
                                    primarySetting || palette.evergreen.aspargus
                                }
                                width={32}
                                minHeight={32}
                                border
                                borderRadius={4}
                            />
                            <FlexDiv
                                backgroundColor={
                                    secondarySetting || palette.vibrant.raspberry
                                }
                                width={32}
                                minHeight={32}
                                border
                                borderRadius={4}
                            />
                        </FlexDiv>
                        <TextDefault themed bold>
                            Change
                        </TextDefault>
                    </ButtonBase>
                </SpaceBetweenDiv>

                <SpaceBetweenDiv py={16} mb={8} alignStart>
                    <FlexDiv column fullWidth>
                        <TextLead bold>Learner player</TextLead>
                        <TextDefault>
                            Customize how training and assessments look for
                            employees, including SCORM packages. Use the
                            variables below to recolor the player, or target a
                            specific part with a class name. The close (X)
                            button is hidden automatically in SCORM.
                        </TextDefault>
                        <FlexDiv column gap={12} style={{ marginTop: 16 }}>
                            <TextInput
                                margin="none"
                                heightSmall
                                stretched
                                label="Font family"
                                placeholder='Calibri, "Segoe UI", sans-serif'
                                value={fontFamily}
                                onChange={(e) => setFontFamily(e.target.value)}
                            />
                            <TextInput
                                margin="none"
                                heightSmall
                                stretched
                                label="Custom font URL (optional)"
                                placeholder="https://fonts.googleapis.com/css2?family=Inter&display=swap"
                                value={fontUrl}
                                onChange={(e) => setFontUrl(e.target.value)}
                            />
                            <CheckboxInput
                                checked={hideClose}
                                onChange={(e) => {
                                    const checked =
                                        typeof e === 'boolean'
                                            ? e
                                            : e?.target?.checked ?? !hideClose;
                                    setHideClose(checked);
                                }}
                                name="hideClose"
                                label={
                                    <TextLead>
                                        Hide the close (X) button in the
                                        employee portal too
                                    </TextLead>
                                }
                                noYPadding
                            />
                            <TextDefault
                                lighter
                                style={{ fontSize: 12, opacity: 0.8 }}
                            >
                                Only visual CSS is applied. Unsupported
                                properties are ignored. Custom variables must
                                start with --ag-.
                            </TextDefault>
                            <details style={{ marginTop: 4 }}>
                                <summary
                                    style={{
                                        cursor: 'pointer',
                                        fontSize: 13,
                                        fontWeight: 600,
                                    }}
                                >
                                    Variables, class names, and what they affect
                                </summary>
                                <div
                                    style={{
                                        fontSize: 12,
                                        lineHeight: 1.5,
                                        marginTop: 8,
                                        opacity: 0.9,
                                    }}
                                >
                                    <div>
                                        <code>--ag-color-primary</code> — Next,
                                        Finish, selected lesson, progress bar,
                                        language menu, accessibility icon, and
                                        answers
                                    </div>
                                    <div>
                                        <code>--ag-color-on-primary</code> —
                                        text on those buttons (white by default)
                                    </div>
                                    <div>
                                        <code>--ag-color-secondary</code> —
                                        extra accent color for your own CSS
                                    </div>
                                    <div>
                                        <code>--ag-color-text</code>,{' '}
                                        <code>--ag-color-background</code>,{' '}
                                        <code>--ag-color-surface</code> — text,
                                        page, and header / sidebar / footer
                                    </div>
                                    <div>
                                        <code>--ag-font-family</code> — player
                                        font
                                    </div>
                                    <div>
                                        <code>.ag-player-btn</code> — Next,
                                        Continue, and Finish.{' '}
                                        <code>.ag-player-btn-prev</code> —
                                        Previous
                                    </div>
                                    <div>
                                        <code>.ag-player-step-active</code> —
                                        the current lesson in the list
                                    </div>
                                    <div>
                                        <code>.ag-player-header</code>,{' '}
                                        <code>.ag-player-sidebar</code>,{' '}
                                        <code>.ag-player-content</code>,{' '}
                                        <code>.ag-player-footer</code> — player
                                        layout
                                    </div>
                                    <div>
                                        <code>.ag-player-close</code> — close
                                        (X) button (already hidden in SCORM)
                                    </div>
                                    <div>
                                        To make videos unskippable, turn off
                                        Allow fast-forward in company settings.
                                    </div>
                                </div>
                            </details>
                            <PlayerCssEditor
                                value={customCss}
                                onChange={setCustomCss}
                                placeholder={
                                    ':root {\n  --ag-color-primary: #003366;\n  --ag-color-on-primary: #ffffff;\n  --ag-color-text: #222222;\n  --ag-font-family: Calibri, "Segoe UI", sans-serif;\n}\n\n.ag-player-btn {\n  border-radius: 4px;\n}\n\n.ag-player-close {\n  display: none;\n}'
                                }
                            />
                            <ThemedButton
                                mode="primary"
                                text="Save player customization"
                                onClick={() =>
                                    onSavePlayer?.({
                                        fontFamily,
                                        fontUrl,
                                        hideClose,
                                        customCss,
                                    })
                                }
                                disabled={saving || !onSavePlayer}
                            />
                        </FlexDiv>
                    </FlexDiv>
                </SpaceBetweenDiv>

                <Link
                    component="button"
                    onClick={() => onResetToDefaults(company_id)}
                >
                    <TextDefault
                        error
                        bold
                    >
                        Reset to default
                    </TextDefault>
                </Link>
            </Block>
            <Block ml={20}>
                <Heading2>Preview</Heading2>
                <TextDefault>
                    This is how buttons and navigation will look in the employee view:
                </TextDefault>
                {saving ? (
                    <FlexDiv marginAuto column alignCenter justifyCenter style={{ minHeight: '200px' }}>
                        <ThemedSpinner />
                    </FlexDiv>
                ) : (
                    <>
                        <FlexDiv marginAuto column>
                            <FlexDiv mb={8}>
                                <ThemedButton
                                    color={primarySetting || palette.evergreen.aspargus}
                                    mode="primary"
                                    text="Button"
                                />
                            </FlexDiv>
                            <FlexDiv mb={16}>
                                <ThemedButton
                                    color={primarySetting || palette.evergreen.aspargus}
                                    mode="secondary"
                                    text="Button"
                                />
                            </FlexDiv>

                        </FlexDiv>
                        <FlexDiv justifyCenter>
                            <ThemedNav clean>
                                <ThemedNavItem 
                                    active={activeNavItem === 0} 
                                    color={secondarySetting || palette.vibrant.raspberry}
                                    onClick={() => setActiveNavItem(0)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <ThemedNavLink>
                                        Navigation Item
                                    </ThemedNavLink>
                                </ThemedNavItem>
                                <ThemedNavItem 
                                    active={activeNavItem === 1} 
                                    color={secondarySetting || palette.vibrant.raspberry}
                                    onClick={() => setActiveNavItem(1)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <ThemedNavLink>
                                        Navigation Item
                                    </ThemedNavLink>
                                </ThemedNavItem>
                            </ThemedNav>
                        </FlexDiv>
                    </>
                )}
            </Block>
        </FlexDiv>
    );
}

export default observer(CompanyAppearanceEditor);
